import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faPhone,
  faEnvelope,
  faHashtag,
  faEye,
  faEyeSlash,
  faSpinner,
  faLock,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Button, Form, InputGroup, Alert, Row, Col } from "@themesberg/react-bootstrap";
import { toast } from "react-toastify";

import { adminAddUser } from "../../../pages/api";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../../routes";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const initialFormData = {
  name: "",
  email: "",
  mobile_number: "",
  password: "",
  confirm_password: "",
  referral_number: "",
};

const getPasswordRules = (password) => {
  const value = String(password || "");
  return {
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
    special: /[@$!%*?&]/.test(value),
    minLength: value.length >= 8,
  };
};

const isPasswordValid = (password) => {
  const rules = getPasswordRules(password);
  return Object.values(rules).every(Boolean);
};

const getPasswordError = (password) => {
  if (!String(password || "").trim()) return "Password is required.";
  if (!isPasswordValid(password)) return "Password must meet all the rules below.";
  return "";
};

const getConfirmPasswordError = (password, confirmPassword) => {
  const confirm = String(confirmPassword || "");
  if (!confirm.trim()) return "Confirm password is required.";
  if (String(password || "") !== confirm) return "Passwords do not match.";
  return "";
};

export default function AdminAddUser() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    mobile_number: false,
    password: false,
    confirm_password: false,
  });

  const passwordRules = useMemo(
    () => getPasswordRules(formData.password),
    [formData.password]
  );

  const unmetPasswordRules = useMemo(() => {
    const rules = [
      { key: "uppercase", label: "At least 1 uppercase letter" },
      { key: "lowercase", label: "At least 1 lowercase letter" },
      { key: "number", label: "At least 1 number" },
      { key: "special", label: "At least 1 special character (@$!%*?&)" },
      { key: "minLength", label: "Minimum 8 characters" },
    ];

    return rules.filter((rule) => !passwordRules[rule.key]);
  }, [passwordRules]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      setErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };

        if (name === "name") {
          nextErrors.name = !value.trim() ? "Name is required." : "";
        }

        if (name === "email") {
          if (!value.trim()) nextErrors.email = "Email is required.";
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
            nextErrors.email = "Enter a valid email address.";
          } else {
            nextErrors.email = "";
          }
        }

        if (name === "mobile_number") {
          if (!value.trim()) nextErrors.mobile_number = "Mobile number is required.";
          else if (!/^[6-9]\d{9}$/.test(value.trim())) {
            nextErrors.mobile_number = "Enter a valid 10-digit mobile number.";
          } else {
            nextErrors.mobile_number = "";
          }
        }

        if (name === "password") {
          nextErrors.password = getPasswordError(value);

          if (touched.confirm_password) {
            nextErrors.confirm_password = getConfirmPasswordError(value, next.confirm_password);
          } else {
            nextErrors.confirm_password = "";
          }
        }

        if (name === "confirm_password") {
          nextErrors.confirm_password = getConfirmPasswordError(next.password, value);
        }

        return nextErrors;
      });

      return next;
    });

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const handleMobileChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 10);

    setFormData((prev) => {
      const next = { ...prev, mobile_number: nextValue };

      setErrors((prevErrors) => ({
        ...prevErrors,
        mobile_number:
          !nextValue.trim()
            ? "Mobile number is required."
            : !/^[6-9]\d{9}$/.test(nextValue.trim())
              ? "Enter a valid 10-digit mobile number."
              : "",
      }));

      return next;
    });

    setTouched((prev) => ({
      ...prev,
      mobile_number: true,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = "Name is required.";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.mobile_number.trim()) {
      nextErrors.mobile_number = "Mobile number is required.";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile_number.trim())) {
      nextErrors.mobile_number = "Enter a valid 10-digit mobile number.";
    }

    nextErrors.password = getPasswordError(formData.password);
    nextErrors.confirm_password = getConfirmPasswordError(
      formData.password,
      formData.confirm_password
    );

    setErrors(nextErrors);
    setTouched({
      name: true,
      email: true,
      mobile_number: true,
      password: true,
      confirm_password: true,
    });

    return Object.keys(nextErrors).every((key) => !nextErrors[key]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile_number: formData.mobile_number.trim(),
        password: formData.password.trim(),
      };

      if (formData.referral_number.trim()) {
        payload.referral_number = formData.referral_number.trim();
      }

      const response = await adminAddUser(payload);

      const message =
        response?.data?.message ||
        response?.message ||
        "User added successfully.";

      toast.success(message);
      setAlertMessage(message);
      setFormData(initialFormData);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setErrors({});
      setTouched({
        name: false,
        email: false,
        mobile_number: false,
        password: false,
        confirm_password: false,
      });
      navigate(Routes?.AdminUserManagement?.path || "/admin-user-management");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to add user.";

      toast.error(message);
      console.error("Add user failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const showPasswordError = touched.password && !!errors.password;
  const showConfirmPasswordError = touched.confirm_password && !!errors.confirm_password;

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          {/* <h2 className="h4 mb-1">Add User</h2> */}
          <h2 className="h4 mb-1">Add User</h2>
        </div>

        <div className="admin-user-header-actions">
        
                  {/* <Button
                      type="button"
                      onClick={() => navigate(`/admin/users/edit/${id}`)}
                      style={ACTIVE_BUTTON_STYLE}
                  >
                      Edit User
                  </Button> */}
        
                  <Button type="button" variant="outline-primary" onClick={() => navigate(-1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    {/* Back */}
                  </Button>
                </div>
      </div>

      {/* {alertMessage ? (
        <Alert variant="info" className="border-0 shadow-sm rounded-4">
          {alertMessage}
        </Alert>
      ) : null} */}

      <form onSubmit={handleSubmit}>
        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Name <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup className={`flex-nowrap ${errors.name ? "invalid-input-group" : ""}`}>
                    <InputGroup.Text className="bg-white">
                      <FontAwesomeIcon icon={faUser} />
                    </InputGroup.Text>
                    <Form.Control
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      isInvalid={!!errors.name}
                      className="border-start-0"
                      placeholder="Enter name"
                    />
                  </InputGroup>
                  {errors.name ? <div className="text-danger small mt-1">{errors.name}</div> : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup className={`flex-nowrap ${errors.email ? "invalid-input-group" : ""}`}>
                    <InputGroup.Text className="bg-white">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </InputGroup.Text>
                    <Form.Control
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                      className="border-start-0"
                      placeholder="Enter email"
                    />
                  </InputGroup>
                  {errors.email ? <div className="text-danger small mt-1">{errors.email}</div> : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Mobile Number <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup
                    className={`flex-nowrap ${errors.mobile_number ? "invalid-input-group" : ""}`}
                  >
                    <InputGroup.Text className="bg-white">
                      <FontAwesomeIcon icon={faPhone} />
                    </InputGroup.Text>
                    <Form.Control
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleMobileChange}
                      isInvalid={!!errors.mobile_number}
                      className="border-start-0"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                    />
                  </InputGroup>
                  {errors.mobile_number ? (
                    <div className="text-danger small mt-1">{errors.mobile_number}</div>
                  ) : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup
                    className={`password-input-group ${showPasswordError ? "invalid-input-group" : ""}`}
                  >
                    <InputGroup.Text className="password-icon-box">
                      <FontAwesomeIcon icon={faLock} className="password-left-icon" />
                    </InputGroup.Text>

                    <Form.Control
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      isInvalid={!!errors.password}
                      className="password-control border-start-0 border-end-0"
                      placeholder="Enter password"
                      autoComplete="new-password"
                    />

                    <Button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        className="password-eye-icon"
                      />
                    </Button>
                  </InputGroup>

                  {showPasswordError ? (
                    <div className="text-danger small mt-2">
                      <div className="mb-2">{errors.password}</div>
                      {errors.password === "Password must meet all the rules below." ? (
                        <ul className="password-rule-list mb-0">
                          {unmetPasswordRules.map((rule) => (
                            <li key={rule.key}>{rule.label}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Confirm Password <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup
                    className={`password-input-group ${
                      showConfirmPasswordError ? "invalid-input-group" : ""
                    }`}
                  >
                    <InputGroup.Text className="password-icon-box">
                      <FontAwesomeIcon icon={faLock} className="password-left-icon" />
                    </InputGroup.Text>

                    <Form.Control
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={handleChange}
                      isInvalid={!!errors.confirm_password}
                      className="password-control border-start-0 border-end-0"
                      placeholder="Re-enter password"
                      autoComplete="new-password"
                    />

                    <Button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEyeSlash : faEye}
                        className="password-eye-icon"
                      />
                    </Button>
                  </InputGroup>

                  {showConfirmPasswordError ? (
                    <div className="text-danger small mt-2">{errors.confirm_password}</div>
                  ) : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Referral Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-white">
                      <FontAwesomeIcon icon={faHashtag} />
                    </InputGroup.Text>
                    <Form.Control
                      name="referral_number"
                      value={formData.referral_number}
                      onChange={handleChange}
                      className="border-start-0"
                      placeholder="Referral Number"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save User"
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </form>

      <style>{`
        .invalid-input-group .form-control,
        .invalid-input-group .input-group-text,
        .invalid-input-group .form-select {
          border-color: #dc3545 !important;
        }

        .invalid-input-group {
          border-radius: 0.5rem;
        }

        .password-input-group {
          display: flex;
          flex-wrap: nowrap;
          border: 1px solid #dee2e6;
          border-radius: 0.5rem;
          overflow: hidden;
          background: #fff;
        }

        .password-input-group:focus-within {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.12);
        }

        .password-input-group.invalid-input-group {
          border-color: #dc3545 !important;
        }

        .password-icon-box {
          background: #fff;
          border: 0;
          border-right: 1px solid #dee2e6;
          // color: #005dfb;
          padding-left: 0.75rem;
          padding-right: 0.75rem;
          min-height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        // .password-left-icon {
        //   color: #005dfb;
        // }

        .password-control {
          border: 0 !important;
          box-shadow: none !important;
          min-height: 38px;
          padding-top: 0.45rem;
          padding-bottom: 0.45rem;
        }

        .password-control:focus {
          box-shadow: none !important;
        }

        .password-toggle-btn {
          min-width: 52px;
          border: 0;
          border-left: 1px solid #dee2e6;
          border-radius: 0;
          background: #e9ecef;
          color: #5f6b7a;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 38px;
          padding: 0;
        }

        .password-input-group.invalid-input-group .password-toggle-btn {
          border-left-color: #dc3545;
        }

        .password-toggle-btn:hover,
        .password-toggle-btn:focus {
          background: #dee2e6;
          color: #495057;
          box-shadow: none;
        }

        .password-eye-icon {
          color: #495057;
        }

        .password-rule-list {
          padding-left: 1.1rem;
        }

        .password-rule-list li {
          margin-bottom: 0.3rem;
        }

        @media (max-width: 767.98px) {
          .password-toggle-btn {
            min-width: 48px;
          }

          .password-icon-box {
            padding-left: 0.7rem;
            padding-right: 0.7rem;
          }
        }
      `}</style>
    </>
  );
}